import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AgeGroup, Classification } from "@alpha/shared";
import { api } from "./api";
import { SESSION_KEY } from "./me";
import type { TeamMatch } from "./searchRows";
import { SITE_SETTINGS_KEY } from "./siteSettings";
import type {
  AdminUser,
  AdminUserRow,
  Bundle,
  Tournament,
  TournamentRoleGrant,
  Venue,
} from "./types";

/** Just enough of a record to label the queue row that points at it. */
export interface NameRef {
  id: string;
  name: string;
}

/** The team side of a queue row — wider, because approving is a decision about a team. */
export interface TeamRef extends NameRef {
  ageGroup: AgeGroup;
  classification: Classification;
}

export interface AdminRegistration {
  id: string;
  tournamentId: string;
  teamId: string;
  divisionId: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  /**
   * Resolved by the API, not looked up in the bundle: a pending registration
   * has no entry, so its team is not among `bundle.teams`. Null once the
   * record it points at is deleted — the row still stands.
   */
  team: TeamRef | null;
  division: NameRef | null;
  /**
   * The pool the coach registered for. Null when they picked none AND when the
   * pool has since been deleted; approval treats those the same, so the row
   * does not tell them apart either.
   */
  pool: NameRef | null;
  status: "pending" | "approved" | "rejected";
  internalNote?: string;
}

export function useAdminTournaments() {
  return useQuery({
    queryKey: ["admin-tournaments"],
    queryFn: () => api.get<{ tournaments: Tournament[] }>("/admin/tournaments"),
  });
}

export function useAdminBundle(tid: string | undefined) {
  return useQuery({
    queryKey: ["admin-bundle", tid],
    enabled: !!tid,
    queryFn: () => api.get<Bundle>(`/admin/tournaments/${tid}/bundle`),
  });
}

/**
 * The approval queue. `enabled` exists because the route is closed to a scorer
 * in the API's capability table: the console shell reads the pending count for
 * its phase rail on every page, and a caller who cannot run the event must not
 * fire a request that 403s on every visit. Off, the query stays absent from
 * the cache — not loading, not failed — so a consumer sees `data === undefined`
 * and nothing else.
 */
export function useAdminRegistrations(
  tid: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["admin-registrations", tid],
    enabled: !!tid && enabled,
    queryFn: () =>
      api.get<{ registrations: AdminRegistration[] }>(`/admin/tournaments/${tid}/registrations`),
  });
}

/**
 * Teams whose name starts with `term`, for Add a team's search-or-create
 * (console spec §5.3). Off until two characters are typed: one letter matches
 * most of the library, and the placeholder says the search is a prefix.
 */
export function useTeamSearch(term: string) {
  const q = term.trim();
  return useQuery({
    queryKey: ["admin-team-search", q],
    enabled: q.length >= 2,
    queryFn: () => api.get<{ teams: TeamMatch[] }>(`/admin/teams/search?q=${encodeURIComponent(q)}`),
  });
}

/** The shared venue library — attached to tournaments and scheduled games. */
export function useAdminVenues() {
  return useQuery({
    queryKey: ["admin-venues"],
    queryFn: () => api.get<{ venues: Venue[] }>("/admin/venues"),
  });
}

/** A mutation that refetches the admin bundle + registrations on success. */
export function useAdminMutation<T>(tid: string, fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-bundle", tid] });
      void qc.invalidateQueries({ queryKey: ["admin-registrations", tid] });
    },
  });
}

/** A mutation that refetches the venue library on success. */
export function useAdminVenueMutation<T>(fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-venues"] }),
  });
}

/**
 * The ADMIN allowlist (spec §5), each row named by the account behind it — the
 * API resolves that from the profile, since a row predating the console stores
 * only a uid.
 */
export function useAdminAdmins() {
  return useQuery({
    queryKey: ["admin-admins"],
    queryFn: () => api.get<{ admins: AdminUser[] }>("/admin/admins"),
  });
}

/**
 * A mutation that refetches the allowlist on success. It also drops the
 * caller's own session so a change to their tier reaches the nav — granting
 * admin to yourself is impossible, but revoking is not, and the console link
 * should not outlive the access.
 */
export function useAdminAllowlistMutation<T>(fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-admins"] });
      void qc.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });
}

/**
 * Every account that has signed up. Cognito is the source of truth rather than
 * the profile table, so the list includes accounts that stopped partway
 * through the signup wizard — the ones an admin is most often hunting for.
 *
 * `truncated` says the API's page cap stopped the walk before the end. It has
 * to be carried rather than inferred: a capped list looks exactly like a
 * complete one, and the search box would then report a real account as
 * nonexistent.
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get<{ users: AdminUserRow[]; truncated?: boolean }>("/admin/users"),
  });
}

/**
 * A mutation that refetches the user list on success, and drops the caller's
 * own session with it — for the same reason the allowlist mutation does: this
 * is the other route by which an account's global tier changes, and the
 * console link should not outlive the access.
 */
export function useAdminUserMutation<T>(fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
      void qc.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });
}

/** Who holds a role on one tournament, each row named by the account behind it. */
export function useTournamentRoles(tid: string | undefined) {
  return useQuery({
    queryKey: ["tournament-roles", tid],
    enabled: !!tid,
    queryFn: () => api.get<{ grants: TournamentRoleGrant[] }>(`/admin/tournaments/${tid}/roles`),
  });
}

/**
 * A mutation that refetches one tournament's grants on success, and the user
 * list with them: the same grant is a chip on the users page, and leaving that
 * stale would keep offering to revoke a role that is already gone.
 */
export function useTournamentRoleMutation<T>(tid: string, fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tournament-roles", tid] });
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

/** A mutation that refetches the site branding (issue #20) on success. */
export function useSiteSettingsMutation<T>(fn: () => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: SITE_SETTINGS_KEY }),
  });
}
